import React, { useState, useEffect } from 'react';
import { InlineWidget, PopupWidget, useCalendlyEventListener } from 'react-calendly';
import Calendar from 'react-calendar';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Container,
  Stack,
  Fade,
  Slide
} from '@mui/material';
import { 
  CalendarToday, 
  Schedule, 
  EventAvailable,
  EventBusy,
  RadioButtonUnchecked,
  Star
} from '@mui/icons-material';
import { 
  format, 
  startOfDay, 
  isSameDay, 
  parseISO, 
  addDays,
  isBefore
} from 'date-fns';
import 'react-calendar/dist/Calendar.css';

const CalendlyOfficial = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [bookedEvents, setBookedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Your Calendly Personal Access Token
  const CALENDLY_TOKEN = 'eyJraWQiOiIxY2UxZTEzNjE3ZGNmNzY2YjNjZWJjY2Y4ZGM1YmFmYThhNjVlNjg0MDIzZjdjMzJiZTgzNDliMjM4MDEzNWI0IiwidHlwIjoiUEFUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiJodHRwczovL2F1dGguY2FsZW5kbHkuY29tIiwiaWF0IjoxNzQ4NDY1Mjk1LCJqdGkiOiI0MTZkNjE4Mi0xZDcyLTQzOWYtOWU5Zi0zMjQxYTM4Nzk4ODciLCJ1c2VyX3V1aWQiOiI1N2ZjZTQ4NC1kYjBlLTQxN2UtYjAxMy1jNjdiMWQ1NDRiZDUifQ.sDTkwOPr-iGu4h8n9pIL6o5YMabLe9fogmKvJxPELfLJdoGgobQDeDNpwrbzdNzFq0Rt13kW2_gnQdwAo_t6mA';

  const calendlyApi = axios.create({
    baseURL: 'https://api.calendly.com',
    headers: {
      'Authorization': `Bearer ${CALENDLY_TOKEN}`,
      'Content-Type': 'application/json'
    }
  });

  // Brand theme - Black background with yellow accents
  const theme = {
    background: '#000000',
    yellow: '#FFD700',
    yellowLight: '#FFFF00',
    yellowDark: '#FFA500',
    white: '#FFFFFF',
    gray: '#666666',
    grayLight: '#999999',
    success: '#00FF00',
    danger: '#FF0000'
  };

  useEffect(() => {
    fetchCalendlyData();
  }, []);

  // Listen for Calendly events
  useCalendlyEventListener({
    onProfilePageViewed: () => console.log("onProfilePageViewed"),
    onDateAndTimeSelected: () => console.log("onDateAndTimeSelected"),
    onEventTypeViewed: () => console.log("onEventTypeViewed"),
    onEventScheduled: (e) => {
      console.log("onEventScheduled", e.data.payload);
      fetchBookedEvents();
      alert('Meeting scheduled successfully!');
    }
  });

  const fetchCalendlyData = async () => {
    try {
      setLoading(true);
      const userResponse = await calendlyApi.get('/users/me');
      const userUri = userResponse.data.resource.uri;
      
      const eventTypesResponse = await calendlyApi.get('/event_types', {
        params: { user: userUri }
      });
      
      const events = eventTypesResponse.data.collection;
      setEventTypes(events);
      
      if (events.length > 0) {
        setSelectedEventType(events[0]);
      }

      await fetchBookedEvents(userUri);
      
    } catch (err) {
      console.error('Error fetching Calendly data:', err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookedEvents = async (userUri) => {
    try {
      const startDate = format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'');
      const endDate = format(addDays(new Date(), 60), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'');
      
      const eventsResponse = await calendlyApi.get('/scheduled_events', {
        params: {
          user: userUri || (await calendlyApi.get('/users/me')).data.resource.uri,
          min_start_time: startDate,
          max_start_time: endDate
        }
      });
      
      setBookedEvents(eventsResponse.data.collection);
    } catch (err) {
      console.error('Error fetching booked events:', err);
    }
  };

  const handleApiError = (err) => {
    if (err.response?.status === 401) {
      setError('Invalid or expired access token. Please check your Calendly credentials.');
    } else if (err.code === 'ERR_NETWORK' || err.message.includes('CORS')) {
      setError('CORS error detected. Using fallback booking method.');
    } else {
      setError(`API Error: ${err.response?.data?.message || err.message}`);
    }
  };

  const isDateBooked = (date) => {
    return bookedEvents.some(event => 
      isSameDay(parseISO(event.start_time), date)
    );
  };

  const isDateAvailable = (date) => {
    if (isBefore(date, startOfDay(new Date()))) {
      return false;
    }
    const dayOfWeek = date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  };

  const getTileClassName = ({ date, view }) => {
    if (view === 'month') {
      if (isDateBooked(date)) {
        return 'booked-date';
      }
      if (isDateAvailable(date)) {
        return 'available-date';
      }
      if (isBefore(date, startOfDay(new Date()))) {
        return 'past-date';
      }
    }
    return '';
  };

  const getTileContent = ({ date, view }) => {
    if (view === 'month') {
      if (isDateBooked(date)) {
        return <div className="date-indicator booked-indicator">●</div>;
      }
      if (isDateAvailable(date)) {
        return <div className="date-indicator available-indicator">●</div>;
      }
    }
    return null;
  };

  const buildCalendlyUrl = (date = null) => {
    if (!selectedEventType) return '';
    const baseUrl = selectedEventType.scheduling_url;
    const params = new URLSearchParams();
    
    if (date && isDateAvailable(date)) {
      params.append('date', format(date, 'yyyy-MM-dd'));
    }
    
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const formatDuration = (duration) => {
    if (duration >= 60) {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${duration}m`;
  };

  const handleDateClick = (date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      // Open Calendly with the selected date
      window.open(buildCalendlyUrl(date), '_blank');
    }
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh',
          background: theme.background,
          py: 8
        }}
      >
        <Box sx={{ position: 'relative', mb: 3 }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: theme.yellow,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <CalendarToday sx={{ color: theme.yellow, fontSize: 24 }} />
          </Box>
        </Box>
        <Typography 
          variant="h5" 
          sx={{ 
            color: theme.yellow,
            fontWeight: 600,
            mb: 1
          }}
        >
          Loading Calendar
        </Typography>
        <Typography variant="body1" sx={{ color: theme.grayLight }}>
          Connecting to Calendly...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: theme.background,
      minHeight: '100vh',
      py: 6
    }}>
      <Container maxWidth="md">
        {/* Header */}
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{
                color: theme.yellow,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Book Your Meeting
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.white,
                fontWeight: 400,
                mb: 4,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Click on any available date to schedule your consultation
            </Typography>
            
            {/* Meeting Type Display */}
            {selectedEventType && (
              <Box sx={{ mb: 4 }}>
                <Chip
                  icon={<Schedule sx={{ color: `${theme.background} !important` }} />}
                  label={`${selectedEventType.name} - ${formatDuration(selectedEventType.duration)}`}
                  sx={{
                    background: theme.yellow,
                    color: theme.background,
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 1
                  }}
                />
              </Box>
            )}
          </Box>
        </Fade>

        {error && (
          <Slide direction="down" in mountOnEnter unmountOnExit>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                borderRadius: 2,
                background: theme.yellowDark,
                color: theme.background,
                '& .MuiAlert-icon': {
                  color: theme.background
                }
              }}
            >
              {error} Direct booking will be used.
            </Alert>
          </Slide>
        )}

        {/* Calendar Section */}
        <Fade in timeout={1000}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              background: theme.background,
              border: `2px solid ${theme.yellow}`,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Legend */}
              <Stack 
                direction="row" 
                spacing={3} 
                justifyContent="center"
                sx={{ mb: 4 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RadioButtonUnchecked sx={{ color: theme.success, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: theme.white, fontWeight: 500 }}>
                    Available
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RadioButtonUnchecked sx={{ color: theme.danger, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: theme.white, fontWeight: 500 }}>
                    Booked
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RadioButtonUnchecked sx={{ color: theme.gray, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: theme.white, fontWeight: 500 }}>
                    Unavailable
                  </Typography>
                </Box>
              </Stack>

              {/* Calendar */}
              <Box sx={{ 
                '& .react-calendar': {
                  width: '100%',
                  border: 'none',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: theme.background
                },
                '& .react-calendar__navigation': {
                  background: theme.yellow,
                  '& button': {
                    color: theme.background,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    border: 'none',
                    background: 'transparent',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.1)'
                    }
                  }
                },
                '& .react-calendar__month-view__weekdays': {
                  background: theme.background,
                  '& .react-calendar__month-view__weekdays__weekday': {
                    color: theme.yellow,
                    fontWeight: 600,
                    padding: '1em'
                  }
                },
                '& .react-calendar__tile': {
                  position: 'relative',
                  padding: '1em',
                  backgroundColor: theme.background,
                  border: `1px solid ${theme.gray}`,
                  color: theme.white,
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    background: theme.gray,
                    transform: 'scale(1.05)'
                  }
                },
                '& .available-date': {
                  backgroundColor: `${theme.background} !important`,
                  border: `2px solid ${theme.success} !important`,
                  color: `${theme.success} !important`,
                  '&:hover': {
                    backgroundColor: `${theme.success} !important`,
                    color: `${theme.background} !important`,
                    transform: 'scale(1.1)',
                    boxShadow: `0 0 20px ${theme.success}`
                  }
                },
                '& .booked-date': {
                  backgroundColor: `${theme.background} !important`,
                  border: `2px solid ${theme.danger} !important`,
                  color: `${theme.danger} !important`,
                  cursor: 'not-allowed',
                  '&:hover': {
                    backgroundColor: `${theme.background} !important`,
                    transform: 'none'
                  }
                },
                '& .past-date': {
                  color: `${theme.gray} !important`,
                  backgroundColor: `${theme.background} !important`,
                  border: `1px solid ${theme.gray} !important`,
                  cursor: 'not-allowed',
                  '&:hover': {
                    backgroundColor: `${theme.background} !important`,
                    transform: 'none'
                  }
                },
                '& .react-calendar__tile--active': {
                  backgroundColor: `${theme.yellow} !important`,
                  color: `${theme.background} !important`,
                  border: `2px solid ${theme.yellow} !important`
                },
                '& .date-indicator': {
                  fontSize: '10px',
                  marginTop: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                },
                '& .available-indicator': {
                  color: theme.success
                },
                '& .booked-indicator': {
                  color: theme.danger
                }
              }}>
                <Calendar
                  onChange={handleDateClick}
                  value={selectedDate}
                  tileClassName={getTileClassName}
                  tileContent={getTileContent}
                  minDate={new Date()}
                  maxDate={addDays(new Date(), 60)}
                  onClickDay={handleDateClick}
                />
              </Box>

              {/* Instructions */}
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.yellow,
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  Click on a green date to book your meeting
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.grayLight,
                    fontStyle: 'italic'
                  }}
                >
                  Available times are Monday - Friday, business hours
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Meeting Types (if multiple) */}
        {eventTypes.length > 1 && (
          <Fade in timeout={1200}>
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: theme.yellow,
                  fontWeight: 700,
                  mb: 3
                }}
              >
                Available Meeting Types
              </Typography>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                flexWrap="wrap"
              >
                {eventTypes.map((eventType) => (
                  <Button
                    key={eventType.uri}
                    variant={selectedEventType?.uri === eventType.uri ? "contained" : "outlined"}
                    onClick={() => setSelectedEventType(eventType)}
                    startIcon={selectedEventType?.uri === eventType.uri ? <Star /> : <Schedule />}
                    sx={{
                      borderColor: theme.yellow,
                      color: selectedEventType?.uri === eventType.uri ? theme.background : theme.yellow,
                      backgroundColor: selectedEventType?.uri === eventType.uri ? theme.yellow : 'transparent',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        backgroundColor: theme.yellow,
                        color: theme.background,
                        borderColor: theme.yellow
                      }
                    }}
                  >
                    {eventType.name} ({formatDuration(eventType.duration)})
                  </Button>
                ))}
              </Stack>
            </Box>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default CalendlyOfficial; 